import { toast } from "react-toastify";
import axios from "axios";
import Bugsnag from "@bugsnag/js";
import { put, select, call, takeEvery, all, fork } from "redux-saga/effects";
import sortBy from "lodash/sortBy";
import isEmpty from "lodash/isEmpty";

import {
  ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE,
  ADD_COLLECTION_ASSETS_FROM_STOCK_ROUTINE,
  ADD_COLLECTION_ASSETS_ROUTINE,
  ADD_COLLECTION_ASSET_ROUTINE,
  ADD_COLLECTION_ROUTINE,
  APPLY_BULK_TAGS_ROUTINE,
  BULK_MOVE_ASSETS_ROUTINE,
  CHANGE_ASSET_PAGINATION_ROUTINE,
  GET_ALL_COLLECTION_ASSETS_ROUTINE,
  CREATE_OR_UPDATE_COLLECTION_USER_ROUTINE,
  DELETE_COLLECTION_ASSET_ROUTINE,
  DELETE_COLLECTION_ASSETS_ROUTINE,
  DELETE_COLLECTION_ROUTINE,
  DELETE_COLLECTION_USER_ROUTINE,
  FILTER_ASSETS_ROUTINE,
  INIT_GLOBALS,
  INIT_MEDIA_LIBRARY_PAGE_ROUTINE,
  LOAD_COLLECTION_COUNTS,
  LOAD_COLLECTIONS_ROUTINE,
  LOAD_LIBRARY_USAGE,
  PRE_DOWNLOAD_ASSET_ROUTINE,
  RELOAD_ASSET_TAGS_ROUTINE,
  SELECT_COLLECTION_ROUTINE,
  SHOW_CREATE_POST_ROUTINE,
  SKED_CORE_GET_ACCOUNTS_ROUTINE,
  TOGGLE_SELECT_NONE_MEDIA,
  UPDATE_COLLECTION_ASSET_ROUTINE,
  UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE,
  UPDATE_COLLECTION_ROUTINE,
  TOGGLE_SIDEBAR_FILTERS,
  SHOW_MODAL,
  RELOAD_ASSET_CURRENT_PAGE,
  DUPLICATE_ASSETS_ROUTINE,
} from "constants/ActionTypes";
import { SOURCE_NAMES, PAGES } from "constants/MediaLibrary";
import Api from "api/media-library";
import { getPagination, getUser } from "selectors/commonSelectors";
import {
  getSelectedCollection,
  getMediaLibraryFilters,
  getPage,
  getMediaLibraryPageSize,
} from "selectors/mediaLibrarySelectors";
import { getFeedCreatorUsername } from "selectors/hashtagFeedSelectors";
import { hideModal } from "actions/modal";
import { changeAssetPagination as changeAssetPaginationAction } from "actions/mediaLibrary";
import { selectCollection, reloadTags } from "actions/mediaLibrary";
import { formatTags } from "reducers/entities/collectionsReducer";

import ngDeps from "@/legacy-services";
import * as EVENTS from "constants/Events";
import { handleError } from "utils/handleError";
import { isLibraryRestricted, type UserWithPlan } from "features/media-library";
import { isEditorUploadsCollection } from "utils/cesdk-editor-collection";

import { trackEventByType, findEventSource } from "./utils";
import { duplicateCollectionAssets } from "api/media-library/frontify";
import type { MediaCollection, StockImage } from "api/media-library/types";
import type {
  AssetTagChanges,
  CollectionFile,
  MediaFilters,
  Source,
} from "shared/types/files";
import type { User } from "features/user/types";
import type { FeedMedia } from "components/MediaLibrary/SourceMedia/FeedGrid/FeedGrid";

// Generic action type used throughout sagas
interface SagaAction<P = unknown> {
  type: string;
  payload: P;
}

// Local interfaces for shapes without exported types
interface CollectionAuthUser {
  _id: string | null;
  description: string;
  create: boolean;
  read: boolean;
  update: boolean;
}

interface Pagination {
  pageSize?: number;
  currentPage?: number;
}

/** @deprecated Use LibraryUsageStats for the raw API shape; this wraps the Axios response */
interface LibraryUsage {
  data: LibraryUsageStats;
}

interface UploadFile {
  url: string;
  source_name: string;
  source_url: string;
  description: string;
  filename: string;
  localId?: string | number;
}

type AssetResult = Omit<CollectionFile, "localId"> & {
  localId?: string | number;
};

/** Paginated response from GET /content-inbox/inbox/:id/assets */
interface PaginatedEntries {
  data: CollectionFile[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

/** Response from GET /content-inbox/entry-stats */
interface LibraryUsageStats {
  auth_key: string;
  asset_size: number;
  count: number;
}

/** Response from GET /content-inbox/inbox/:id/tags */
interface CollectionTag {
  tag: string;
  count: number;
}

/** Response from GET /content-inbox/inbox/:id/auth */
interface InboxAuth {
  _id: string;
  description: string;
  active: boolean;
  inbox_id?: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  expires: string;
  created: string;
  token: string;
}

/** Paginated response from GET /content-inbox/inbox */
interface PaginatedCollections {
  data: MediaCollection[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

const mlApi = new Api();

export function* initMediaLibraryPage(action: SagaAction): Generator {
  const { $rootScope } = ngDeps;
  const { eventService } = ngDeps;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_LOAD_COLLECTIONS_PAGE,
    useServices: ["AMP"],
  });
  try {
    yield put({ type: INIT_MEDIA_LIBRARY_PAGE_ROUTINE.REQUEST });
    yield put({
      type: INIT_GLOBALS,
      payload: {
        user: $rootScope.user,
      },
    });
    yield fork(loadLibraryUsage);
    yield call(loadCollections, action);
    yield put({ type: SKED_CORE_GET_ACCOUNTS_ROUTINE.TRIGGER });
    yield put({ type: INIT_MEDIA_LIBRARY_PAGE_ROUTINE.SUCCESS });
  } catch (error) {
    yield put({
      type: INIT_MEDIA_LIBRARY_PAGE_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

export function* loadLibraryUsage(): Generator {
  try {
    const libraryUsage = (yield call([
      mlApi,
      mlApi.getLibraryUsage,
    ])) as LibraryUsage;
    const { asset_size, count } = libraryUsage.data;
    yield put({
      type: LOAD_LIBRARY_USAGE,
      payload: {
        asset_size,
        asset_count: count,
      },
    });
  } catch (error) {
    handleError(error);
  }
}

// Collection Sagas
export function* loadCollections(_action: SagaAction): Generator {
  yield put({ type: LOAD_COLLECTIONS_ROUTINE.REQUEST });
  const selectedCollection = (yield select(
    getSelectedCollection
  )) as MediaCollection | null;
  const user = (yield select(getUser)) as User;
  try {
    const collectionResp = (yield call([
      mlApi,
      mlApi.getCollectionsMetadata,
    ])) as {
      data: PaginatedCollections;
    };
    let collections = sortBy(collectionResp.data.data, ["name"]);

    // Filter out Editor Uploads collection (managed by ceSDK editor)
    collections = collections.filter(
      (collection: MediaCollection) => !isEditorUploadsCollection(collection)
    );

    if (isLibraryRestricted(user as UserWithPlan)) {
      collections = collections.filter(
        (collection: MediaCollection) => collection.isSystemInbox
      );
    }

    yield put({
      type: LOAD_COLLECTIONS_ROUTINE.SUCCESS,
      payload: {
        collections,
      },
    });

    yield fork(loadCollectionCounts, collections);

    if (collections.length > 0) {
      const collectionId = selectedCollection?._id
        ? selectedCollection._id
        : collections[0]._id;
      yield put(selectCollection(collectionId, true));
    }
  } catch (error) {
    yield put({
      type: LOAD_COLLECTIONS_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

export function* loadCollectionCounts(
  collections: MediaCollection[]
): Generator {
  try {
    const countsResp = (yield call([mlApi, mlApi.getCollectionCounts])) as {
      data: Array<{ _id: string; asset_count: number }>;
    };

    const countsById = new Map(
      countsResp.data.map((count) => [count._id, count.asset_count])
    );

    yield put({
      type: LOAD_COLLECTION_COUNTS,
      payload: {
        counts: collections.map((collection) => ({
          _id: collection._id,
          asset_count: countsById.get(collection._id) ?? 0,
        })),
      },
    });
  } catch (error) {
    yield put({
      type: LOAD_COLLECTION_COUNTS,
      payload: {
        counts: collections.map((collection) => ({
          _id: collection._id,
          asset_count: 0,
        })),
      },
    });
    handleError(error);
  }
}

export function* selectCollectionSaga(
  action: SagaAction<{ id: string; skipTracking: boolean }>
): Generator {
  const { id, skipTracking } = action.payload;
  const currentSelection = (yield select(
    getSelectedCollection
  )) as MediaCollection | null;
  const pagination = (yield select(getPagination)) as Pagination;

  const { eventService } = ngDeps;

  if (!skipTracking) {
    eventService.trackEvent({
      eventName: EVENTS.LIBRARY_SELECT_COLLECTION,
      useServices: ["AMP"],
    });
  }

  yield put({
    type: SELECT_COLLECTION_ROUTINE.REQUEST,
    payload: action.payload,
  });

  try {
    const user = (yield select(getUser)) as User;
    const userResp =
      user.role === "admin"
        ? ((yield call([mlApi, mlApi.getCollectionAuth], id)) as {
            data: InboxAuth[];
          })
        : { data: [] };
    const pageSize = (yield select(getMediaLibraryPageSize)) as number;
    const pageToLoad =
      currentSelection?._id === id && pagination?.currentPage
        ? pagination.currentPage
        : 1;
    const {
      tags,
      name,
      description,
      favorite,
      mediaType,
      sourceName,
      sort,
      usage,
    } = (yield select(getMediaLibraryFilters)) as MediaFilters;

    const entriesResp = (yield call(
      [mlApi, mlApi.getEntries] as [unknown, (...args: unknown[]) => unknown],
      id,
      pageToLoad,
      pageSize,
      tags,
      name,
      description,
      favorite,
      mediaType,
      sourceName,
      usage,
      true, // withDownloadUrl - always want this
      sort
    )) as { data: PaginatedEntries };
    const tagsResp = (yield call([mlApi, mlApi.getTags], id)) as {
      data: CollectionTag[];
    };

    yield put({
      type: SELECT_COLLECTION_ROUTINE.SUCCESS,
      payload: {
        id: id,
        entries: entriesResp.data,
        users: userResp.data,
        tags: formatTags(tagsResp.data),
      },
    });
  } catch (error) {
    yield put({
      type: SELECT_COLLECTION_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

export function* addCollection(
  action: SagaAction<{ name: string; fromInit?: boolean }>
): Generator {
  const { name, fromInit = false } = action.payload;
  const { eventService } = ngDeps;

  if (!fromInit) {
    eventService.trackEvent({ eventName: EVENTS.LIBRARY_CREATE_COLLECTION });
  }

  yield put({ type: ADD_COLLECTION_ROUTINE.REQUEST });
  try {
    const data = (yield call(
      [mlApi, mlApi.createCollection] as [
        unknown,
        (...args: unknown[]) => unknown,
      ],
      name
    )) as { data: MediaCollection };
    const collection = { ...data.data, asset_count: 0 };

    // // Create each user that was passed in
    // let userResponses = [];
    // if (extAuth && users.length > 0) {
    //   let collectionId = data.data._id;
    //   userResponses = yield all(
    //     _.map(users, user => {
    //       console.log("inside yield all", user);
    //       if (user.description.length > 0) {
    //         return call(mlApi.createCollectionAuthUser, collectionId, user);
    //       }
    //     })
    //   );
    // }
    yield put({
      type: ADD_COLLECTION_ROUTINE.SUCCESS,
      payload: {
        collection,
        // users: userResponses,
      },
    });
    yield put(selectCollection(data.data._id, true));
  } catch (error) {
    yield put({
      type: ADD_COLLECTION_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

export function* deleteCollection(
  action: SagaAction<{ id: string }>
): Generator {
  const { id } = action.payload;
  const { eventService } = ngDeps;

  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_DELETE_COLLECTION,
    useServices: ["AMP"],
  });

  yield put({
    type: DELETE_COLLECTION_ROUTINE.REQUEST,
    payload: action.payload,
  });
  try {
    yield call([mlApi, mlApi.deleteCollection], id);
    yield put({
      type: DELETE_COLLECTION_ROUTINE.SUCCESS,
      payload: action.payload,
    });
    toast.success("Successfully deleted collection.");
  } catch (error) {
    yield put({
      type: DELETE_COLLECTION_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  } finally {
    yield put(hideModal());
  }
}

export function* updateCollection(
  action: SagaAction<{ collection: MediaCollection }>
): Generator {
  const { collection } = action.payload;
  const { _id, name, active, preferences } = collection;
  const { eventService } = ngDeps;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_UPDATE_COLLECTION,
    useServices: ["AMP"],
  });

  yield put({ type: UPDATE_COLLECTION_ROUTINE.REQUEST });

  try {
    const data = (yield call([mlApi, mlApi.updateCollection], _id, {
      name,
      active,
      preferences,
    })) as { data: { success: boolean; message?: string } };
    if (!data.data.success) {
      throw new Error(data.data.message);
    }
    yield put({
      type: UPDATE_COLLECTION_ROUTINE.SUCCESS,
      payload: {
        collection,
      },
    });
    toast.success("Successfully updated collection.");
  } catch (error) {
    yield put({
      type: UPDATE_COLLECTION_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

// Collection User Sagas
export function* deleteCollectionUser(
  action: SagaAction<{ collectionId: string; collectionUserId: string }>
): Generator {
  const { collectionId, collectionUserId } = action.payload;
  yield put({ type: DELETE_COLLECTION_USER_ROUTINE.REQUEST });
  try {
    yield call(
      [mlApi, mlApi.deleteCollectionAuthUser] as [
        unknown,
        (...args: unknown[]) => unknown,
      ],
      collectionId,
      collectionUserId
    );
    yield put({
      type: DELETE_COLLECTION_USER_ROUTINE.SUCCESS,
      payload: {
        collectionId,
        collectionUserId,
      },
    });
  } catch (error) {
    yield put({
      type: DELETE_COLLECTION_USER_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  } finally {
    yield put(hideModal());
  }
}

export function* createOrUpdateCollectionUser(
  action: SagaAction<{
    collection: MediaCollection;
    collectionUser: CollectionAuthUser;
  }>
): Generator {
  const { collection, collectionUser } = action.payload;

  yield put({
    type: CREATE_OR_UPDATE_COLLECTION_USER_ROUTINE.REQUEST,
    payload: { collection, collectionUser },
  });

  try {
    const { description, create, read, update } = collectionUser;
    const payload = { description, create, read, update };

    const userResp = (yield collectionUser._id === null
      ? call([mlApi, mlApi.createCollectionAuthUser], collection._id, payload)
      : call(
          [mlApi, mlApi.updateCollectionAuthUser],
          collection._id,
          collectionUser._id,
          payload
        )) as { data: CollectionAuthUser };

    yield put({
      type: CREATE_OR_UPDATE_COLLECTION_USER_ROUTINE.SUCCESS,
      payload: {
        call: collectionUser._id === null ? "create" : "update",
        collection: collection,
        collectionUser: userResp.data,
      },
    });
  } catch (error) {
    yield put({
      type: CREATE_OR_UPDATE_COLLECTION_USER_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  } finally {
    yield put(hideModal());
  }
}

// Collection Asset Sagas
export function* addCollectionAssetsFromFeed(
  action: SagaAction<{
    collectionId: string;
    feedItems: FeedMedia[];
    location: string;
    done?: ((results: AssetResult[] | null) => void) | null;
  }>
): Generator {
  const files: Array<File & Record<string, unknown>> = [];
  const { collectionId, feedItems, location, done } = action.payload;
  const page = (yield select(getPage)) as string;

  yield put({
    type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.REQUEST,
  });

  try {
    for (const feedItem of feedItems) {
      const resp = (yield call(axios.get, feedItem.media_url, {
        responseType: "blob",
        timeout: 30_000,
      })) as { data: Blob };
      const blobType = resp.data.type;
      const type = blobType?.split("/")?.pop(); // image/jpg => jpg

      const filename = `${feedItem.id}.${type}`;
      // File extended with custom properties for saga pipeline
      const file = new File([resp.data], filename, {
        type: blobType,
      }) as File & Record<string, unknown>;
      file.source_url = feedItem.permalink;

      const creator = (yield select(getFeedCreatorUsername)) as string;
      file.source_name = {
        [PAGES.TAGGED_IN]:
          SOURCE_NAMES.TAGGED_IN_FEED +
          (feedItem.username ? ` (${feedItem.username})` : ``),
        [PAGES.HASHTAGS]: SOURCE_NAMES.HASHTAG_FEED,
        [PAGES.CREATOR]: `${creator}'s feed`,
      }[page];
      file.description = feedItem.caption ? feedItem.caption : "";

      // We don't have username for hashtag search, but do for mentions, tagged in
      if (feedItem.username) {
        file.description += `\n\n📸 : @${feedItem.username}`;
      }

      if (feedItem.id) {
        file.localId = feedItem.id;
      }

      files.push(file);
    }
    const results = (yield call(addCollectionAssets, {
      type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.REQUEST,
      payload: {
        collectionId,
        files,
        fromFeed: true,
        location,
      },
    })) as AssetResult[];

    // Validate results before calling done
    if (!results || !Array.isArray(results) || results.length === 0) {
      const error = new Error(
        "addCollectionAssets returned invalid results: " +
          JSON.stringify(results)
      );
      console.error("Feed save validation failed:", {
        results,
        filesRequested: files.length,
        collectionId,
      });
      throw error;
    }

    // Verify each result has required fields
    const invalidResults = results.filter(
      (r: AssetResult) => !r._id || !r.asset_id || !r.localId
    );
    if (invalidResults.length > 0) {
      console.error("Invalid results from addCollectionAssets (feed):", {
        invalid: invalidResults.map((r: AssetResult) => ({
          _id: r._id,
          asset_id: r.asset_id,
          localId: r.localId,
          name: r.name,
        })),
        invalidCount: invalidResults.length,
        total: results.length,
      });
      throw new Error(
        `${invalidResults.length} of ${results.length} assets missing required fields`
      );
    }

    // Log success for debugging
    console.log("Feed assets saved successfully:", {
      count: results.length,
      collectionId,
      sampleResult: results[0]
        ? {
            _id: results[0]._id,
            asset_id: results[0].asset_id,
            localId: results[0].localId,
          }
        : null,
    });

    yield put({
      type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.SUCCESS,
      payload: {
        collectionId,
        files,
      },
    });

    toast.success(
      `Successfully saved ${files.length} item${files.length === 1 ? "" : "s"} to collection`
    );

    done && done(results);
  } catch (error) {
    console.error("addCollectionAssetsFromFeed error:", {
      error,
      message: (error as Error).message,
      collectionId,
      fileCount: files?.length,
    });

    yield put({
      type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });

    toast.error(
      "Failed to save items to collection: " +
        ((error as Error).message || "Unknown error. Please try again.")
    );

    handleError(error);

    // Call done with null to signal failure
    done && done(null);

    // Do NOT re-throw — letting the error bubble up kills all sibling takeEvery
    // watchers (inbox, conversations, etc.) in the root saga.
  }
}

export function* getAllCollectionAssets(
  action: SagaAction<{ maxFiles: number }>
): Generator {
  const { maxFiles } = action.payload;
  const page = 1;
  const selectedCollection = (yield select(
    getSelectedCollection
  )) as MediaCollection;

  try {
    const entriesResp = (yield call(
      [mlApi, mlApi.getEntries] as [unknown, (...args: unknown[]) => unknown],
      selectedCollection._id,
      page,
      maxFiles
    )) as { data: PaginatedEntries };

    yield put({
      type: GET_ALL_COLLECTION_ASSETS_ROUTINE.SUCCESS,
      payload: {
        assets: entriesResp.data.data,
      },
    });
  } catch (error) {
    handleError(error);
  }
}

export function* addCollectionAssetsFromStock(
  action: SagaAction<{
    collectionId: string;
    stockItems: StockImage[];
    location: string;
    done?: ((results: AssetResult[] | null) => void) | null;
  }>
): Generator {
  const { collectionId, stockItems, location, done } = action.payload;

  const imageSrcKey = "original" as const;

  // helper
  const getExtension = (url: string): string =>
    url.split(/[#?]/)[0].split(".").pop()?.trim() ?? "jpg";

  const files: UploadFile[] = [];
  try {
    for (const item of stockItems) {
      const url = item.src[imageSrcKey];
      const extension = getExtension(url);
      const filename = `${item.id}.${extension}`;
      const file: UploadFile = {
        url,
        source_name: SOURCE_NAMES.STOCKS,
        source_url: item.url, // Pexels page URL for attribution (stable, per Pexels API terms)
        description: item.alt || "",
        filename,
        localId: item?.id,
      };
      files.push(file);
    }

    // Intentionally reuses ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE — the
    // hashtagFeedMediaReducer listens to its SUCCESS to clear selectedItems,
    // which applies to both feed and stock saves.
    const results = (yield call(addCollectionAssetsViaUrl, {
      type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.REQUEST,
      payload: {
        collectionId,
        files,
        fromFeed: true,
        location,
      },
    })) as AssetResult[];

    if (results && Array.isArray(results) && results.length > 0) {
      // Warn about malformed results but don't crash — still dispatch success
      const invalidResults = results.filter(
        (r: AssetResult) => !r._id || !r.asset_id || !r.localId
      );
      if (invalidResults.length > 0) {
        const msg = `addCollectionAssetsFromStock: ${invalidResults.length} of ${results.length} results missing required fields`;
        console.warn(msg, {
          invalid: invalidResults.map((r: AssetResult) => ({
            _id: r._id,
            asset_id: r.asset_id,
            localId: r.localId,
            name: r.name,
          })),
        });
        Bugsnag.notify(new Error(msg));
      }

      yield put({
        type: ADD_COLLECTION_ASSET_ROUTINE.SUCCESS,
        payload: {
          collectionId,
          assetsAdded: stockItems.length,
        },
      });

      yield put({
        type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.SUCCESS,
        payload: {
          collectionId,
          files,
        },
      });

      done && done(results);
    } else {
      // addCollectionAssetsViaUrl already shows per-file error toasts; log for
      // debugging but do not crash the saga tree.
      console.error("addCollectionAssetsFromStock: no assets were saved", {
        results,
        filesRequested: files.length,
        collectionId,
      });

      yield put({
        type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.FAILURE,
        error: true,
        payload: new Error("No assets were saved successfully"),
      });

      done && done(null);
    }
  } catch (error) {
    console.error("addCollectionAssetsFromStock error:", {
      error,
      message: (error as Error).message,
      collectionId,
      fileCount: files?.length,
    });

    yield put({
      type: ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });

    toast.error(
      "Failed to save stock images to collection: " +
        ((error as Error).message || "Unknown error. Please try again.")
    );

    handleError(error);

    // Call done with null to signal failure
    done && done(null);

    // Do NOT re-throw — letting the error bubble up kills all sibling takeEvery
    // watchers (inbox, conversations, etc.) in the root saga.
  }
}

export function* addCollectionAssetsViaUrl(
  action: SagaAction<{
    collectionId: string;
    files: UploadFile[];
    fromFeed?: boolean;
    fromInitialLibrary?: boolean;
    location?: string;
  }>
): Generator {
  const {
    collectionId,
    files,
    fromFeed = false,
    fromInitialLibrary = false,
    location = "library",
  } = action.payload;
  const { eventService } = ngDeps;
  const page = (yield select(getPage)) as string;
  const user = (yield select(getUser)) as User;

  yield put({
    type: ADD_COLLECTION_ASSETS_ROUTINE.REQUEST,
    payload: {
      collectionId,
      files,
    },
  });

  const eventSource = findEventSource(page);

  const successfulFiles: UploadFile[] = [];
  const failedFiles: UploadFile[] = [];
  const failedFileErrors: unknown[] = [];
  const apiResults: AssetResult[] = [];
  // Upload all files in parallel not in series
  const promises = files.map((file) => {
    return mlApi
      .addEntryFromUrl(collectionId, file)
      .then((result) => {
        if (!result.data._id) {
          throw new Error("Uploaded collection asset is missing an id");
        }
        apiResults.push({
          ...result?.data,
          localId: file?.localId,
        } as AssetResult);
        successfulFiles.push(file);
      })
      .catch((error: unknown) => {
        const axiosErr = error as {
          response?: { data?: { message?: string }; status?: number };
          message?: string;
        };
        const serverMsg = axiosErr?.response?.data?.message;
        console.error("error adding file to library", {
          filename: file.filename,
          url: file.url,
          serverMessage:
            typeof serverMsg === "string" ? serverMsg : axiosErr?.message,
          status: axiosErr?.response?.status,
        });
        failedFileErrors.push(error);
        failedFiles.push(file);
      });
  });
  yield all(promises);

  if (successfulFiles.length > 0) {
    yield put({
      type: ADD_COLLECTION_ASSETS_ROUTINE.SUCCESS,
    });
  } else {
    yield put({
      type: ADD_COLLECTION_ASSETS_ROUTINE.FAILURE,
      error: true,
      payload: new Error(`All ${files.length} file(s) failed to upload`),
    });
  }
  if (!fromInitialLibrary && successfulFiles.length > 0) {
    toast.success(
      `Successfully added ${successfulFiles.length}/${files.length} asset${
        files.length > 1 ? "s" : ""
      } to collection.`
    );
  }

  if (!fromInitialLibrary && failedFiles.length > 0) {
    toast.error(
      `Failed to add ${failedFiles.length}/${files.length} asset${
        files.length > 1 ? "s" : ""
      } to collection.`
    );
  }
  if (failedFileErrors && failedFileErrors.length > 0) {
    failedFileErrors.forEach((err) => {
      handleError(err);
    });
  }

  if (!fromInitialLibrary) {
    eventService.trackEvent({
      eventName: EVENTS.LIBRARY_ADD_ASSETS,
      metadata: {
        total: files.length,
        success: successfulFiles.length,
        failed: failedFiles.length,
        source: eventSource,
        location,
      },
      user,
    });
  }

  if (!fromFeed && !fromInitialLibrary) {
    yield put(selectCollection(collectionId, true));
  }
  yield put(hideModal());
  return apiResults;
}

export function* addCollectionAssets(
  action: SagaAction<{
    collectionId: string;
    files: Array<File & Record<string, unknown>>;
    fromFeed?: boolean;
    fromInitialLibrary?: boolean;
    location?: string;
  }>
): Generator {
  const {
    collectionId,
    files,
    fromFeed = false,
    fromInitialLibrary = false,
    location = "library",
  } = action.payload;
  const { eventService } = ngDeps;
  const page = (yield select(getPage)) as string;
  const user = (yield select(getUser)) as User;
  const eventSource = findEventSource(page);

  yield put({
    type: ADD_COLLECTION_ASSETS_ROUTINE.REQUEST,
    payload: {
      collectionId,
      files,
    },
  });

  const successfulFiles: Array<File & Record<string, unknown>> = [];
  const failedFiles: Array<File & Record<string, unknown>> = [];
  const failedFileErrors: unknown[] = [];
  const apiResults: AssetResult[] = [];
  // Upload all files in parallel not in series
  const promises = files.map((file) => {
    return mlApi
      .addEntry(collectionId, file, {
        name: file.name,
        description: file.description ? (file.description as string) : "",
        source_name: file.source_name
          ? (file.source_name as string)
          : SOURCE_NAMES.UPLOAD,
        source_url: file.source_url ? (file.source_url as string) : "",
        tags: file.tags ? (file.tags as string[]) : [],
      })
      .then((res: { data: CollectionFile }) => {
        successfulFiles.push(file);
        apiResults.push({
          ...res?.data,
          localId: file?.localId as string | number | undefined,
        });
      })
      .catch((error: unknown) => {
        const axiosErr = error as {
          response?: { data?: { message?: string }; status?: number };
          message?: string;
        };
        const serverMsg = axiosErr?.response?.data?.message;
        console.error("error adding file to library", {
          filename: file.name,
          serverMessage:
            typeof serverMsg === "string" ? serverMsg : axiosErr?.message,
          status: axiosErr?.response?.status,
        });
        failedFileErrors.push(error);
        failedFiles.push(file);
      });
  });
  yield all(promises);

  if (successfulFiles.length > 0) {
    yield put({
      type: ADD_COLLECTION_ASSETS_ROUTINE.SUCCESS,
    });
  } else {
    yield put({
      type: ADD_COLLECTION_ASSETS_ROUTINE.FAILURE,
      error: true,
      payload: new Error(`All ${files.length} file(s) failed to upload`),
    });
  }
  if (!fromInitialLibrary && successfulFiles.length > 0) {
    yield put({
      type: ADD_COLLECTION_ASSET_ROUTINE.SUCCESS,
      payload: {
        collectionId,
        assetsAdded: successfulFiles.length,
      },
    });

    toast.success(
      `Successfully added ${successfulFiles.length}/${files.length} asset${
        files.length > 1 ? "s" : ""
      } to collection.`
    );
  }

  if (!fromInitialLibrary && failedFiles.length > 0) {
    toast.error(
      `Failed to add ${failedFiles.length}/${files.length} asset${
        files.length > 1 ? "s" : ""
      } to collection.`
    );
  }
  if (failedFileErrors && failedFileErrors.length > 0) {
    failedFileErrors.map((err) => {
      handleError(err);
    });
  }

  if (!fromInitialLibrary) {
    eventService.trackEvent({
      eventName: EVENTS.LIBRARY_ADD_ASSETS,
      metadata: {
        total: files.length,
        success: successfulFiles.length,
        failed: failedFiles.length,
        source: eventSource,
        location,
      },
      user,
    });
  }

  if (!fromFeed && !fromInitialLibrary) {
    yield put(selectCollection(collectionId, true));
  }
  yield put(hideModal());

  return apiResults;
}

export function* deleteCollectionAsset(
  action: SagaAction<{
    collectionId?: string;
    entryId?: string;
    assetId: string;
    fromBulk?: boolean;
  }>
): Generator {
  const { collectionId, entryId, assetId, fromBulk } = action.payload;
  const collection = (yield select(getSelectedCollection)) as MediaCollection;

  yield put({
    type: DELETE_COLLECTION_ASSET_ROUTINE.REQUEST,
    payload: action.payload,
  });
  try {
    yield call([mlApi, mlApi.deleteEntry], assetId);
    yield put({
      type: DELETE_COLLECTION_ASSET_ROUTINE.SUCCESS,
      payload: {
        entryId,
        assetId,
        collectionId,
      },
    });
    if (!fromBulk) {
      toast.success("Successfully deleted asset from collection.");
      yield put(selectCollection(collection._id, true));
      yield put(reloadTags());
    }
  } catch (error) {
    yield put({
      type: DELETE_COLLECTION_ASSET_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
    if (!fromBulk) {
      toast.error("Failed to delete asset from collection.");
    }
  }
}

export function* deleteCollectionAssets(
  action: SagaAction<{ assets: CollectionFile[] }>
): Generator {
  const { assets } = action.payload;
  const { eventService } = ngDeps;
  const collection = (yield select(getSelectedCollection)) as MediaCollection;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_DELETE_ASSETS,
    metadata: {
      count: assets.length,
    },
    useServices: ["AMP"],
  });

  yield put({
    type: DELETE_COLLECTION_ASSETS_ROUTINE.REQUEST,
    payload: { assets, fromBulk: true },
  });
  for (const asset of assets) {
    yield call(deleteCollectionAsset, {
      type: DELETE_COLLECTION_ASSET_ROUTINE.TRIGGER,
      payload: {
        assetId: asset.asset_id,
        fromBulk: true,
      },
    });
  }

  yield put(selectCollection(collection._id, true));

  yield put({
    type: DELETE_COLLECTION_ASSETS_ROUTINE.SUCCESS,
    payload: { assets },
  });
  toast.success(
    `Successfully deleted ${assets.length} asset${
      assets.length > 1 ? "s" : ""
    } from collection.`
  );
  yield put(reloadTags());
}

type FavoriteEntriesState = {
  byId: Record<string, CollectionFile>;
  favoriteUpdates?: Record<string, boolean>;
};

const getFavoriteEntries = (state: {
  entities: { collectionEntries: FavoriteEntriesState };
}) => state.entities.collectionEntries;

function* updateAssetFavorite(
  asset: CollectionFile,
  favorite: boolean
): Generator {
  const assetId = asset.asset_id;
  const entries = (yield select(getFavoriteEntries)) as FavoriteEntriesState;
  let confirmedFavorite =
    entries.byId[assetId]?.favorite ?? asset.favorite ?? false;
  yield put({
    type: UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE.REQUEST,
    payload: { assetId, favorite },
  });
  // An in-flight save owns this asset. Its loop picks up the latest optimistic
  // value, so rapid toggles cannot reach the server out of order.
  if (entries.favoriteUpdates?.[assetId]) return;
  let requestedFavorite = favorite;
  try {
    while (true) {
      yield call(
        [mlApi, mlApi.updateEntry],
        assetId,
        undefined,
        undefined,
        undefined,
        null,
        undefined,
        undefined,
        requestedFavorite
      );
      confirmedFavorite = requestedFavorite;
      const current = (yield select(
        getFavoriteEntries
      )) as FavoriteEntriesState;
      requestedFavorite = current.byId[assetId]?.favorite ?? confirmedFavorite;
      if (requestedFavorite === confirmedFavorite) break;
    }
    yield put({
      type: UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE.SUCCESS,
      payload: { assetId },
    });
  } catch (error) {
    yield put({
      type: UPDATE_COLLECTION_ASSET_FAVORITE_ROUTINE.FAILURE,
      payload: { assetId, favorite: confirmedFavorite },
    });
    handleError(error);
    toast.error("Failed to update favourite. Please try again.");
  }
}

export function* updateCollectionAsset(
  action: SagaAction<{
    asset: CollectionFile;
    file?: File | null;
    filename?: string;
    description?: string;
    tags?: string[];
    source?: Source;
    sourceNotes?: string;
    favorite?: boolean;
    detailsSheet?: boolean;
    tagChanges?: AssetTagChanges;
  }>,
  fromBulk = false
): Generator {
  const {
    asset,
    file,
    filename,
    description,
    tags,
    source,
    sourceNotes,
    favorite,
    detailsSheet,
    tagChanges,
  } = action.payload;
  const { eventService } = ngDeps;
  if (!fromBulk) {
    eventService.trackEvent({
      eventName: EVENTS.LIBRARY_UPDATE_ASSET,
      useServices: ["AMP"],
    });
  }

  if (!isEmpty(file)) {
    // newAsset
  }

  if (
    typeof favorite === "boolean" &&
    isEmpty(file) &&
    filename == null &&
    description == null &&
    tags == null &&
    source?.name == null &&
    sourceNotes == null &&
    !detailsSheet
  ) {
    yield* updateAssetFavorite(asset, favorite);
    return;
  }

  yield put({ type: UPDATE_COLLECTION_ASSET_ROUTINE.REQUEST });
  try {
    // Get the existing asset from state to preserve used fields
    const collectionEntries = (yield select(
      (state: {
        entities: {
          collectionEntries: { byId: Record<string, CollectionFile> };
        };
      }) => state.entities.collectionEntries.byId
    )) as Record<string, CollectionFile>;
    const existingAsset = collectionEntries[asset.asset_id];

    //TODO will need to do something different when updating img
    // const downloadResp = yield call(mlApi.downloadEntry, assetId);
    // const file = new File([downloadResp.data], "medial-library-asset.jpg");
    const newFile = isEmpty(file) ? null : file;
    yield call(
      { context: mlApi, fn: mlApi.updateEntry },
      asset.asset_id,
      filename ?? undefined,
      description ?? undefined,
      tags ?? undefined,
      newFile,
      source?.name ?? undefined,
      sourceNotes,
      favorite,
      tagChanges
    );

    const getAssetResp = (yield call(
      [mlApi, mlApi.getEntry],
      asset.asset_id
    )) as { data: CollectionFile };

    // Preserve the used fields from the existing asset since mlApi.getEntry doesn't include them
    const updatedAsset = {
      ...getAssetResp.data,
      // Preserve usage information from the existing asset
      usedInUpcomingPosts: existingAsset?.usedInUpcomingPosts || [],
      usedInDraftPosts: existingAsset?.usedInDraftPosts || [],
    };

    yield put({
      type: UPDATE_COLLECTION_ASSET_ROUTINE.SUCCESS,
      meta: { detailsSheet },
      payload: {
        asset: updatedAsset,
      },
    });

    if (!fromBulk) {
      yield put(reloadTags());
      toast.success("Successfully updated asset.");
    }
  } catch (error) {
    yield put({
      type: UPDATE_COLLECTION_ASSET_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });

    if (fromBulk) {
      handleError(error);
    } else {
      handleError(error);
      toast.error("Failed to update asset.");
    }
  }
}

export function* showCreatePost(
  action: SagaAction<{ assets: CollectionFile[] }>
): Generator {
  const { assets } = action.payload;
  const { loadingSpinnerService, modalService } = ngDeps;
  const { eventService } = ngDeps;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_CREATE_POST,
    metadata: { count: assets.length },
  });

  const files: Array<File & Record<string, unknown>> = [];

  loadingSpinnerService.showLoading();
  yield put({ type: SHOW_CREATE_POST_ROUTINE.REQUEST });
  try {
    for (const asset of assets) {
      yield put({
        type: PRE_DOWNLOAD_ASSET_ROUTINE.REQUEST,
        payload: { asset },
      });
      const resp = (yield call(
        [mlApi, mlApi.downloadEntry],
        asset.asset_id
      )) as { data: Blob };
      const filename =
        asset.name && asset.name.length > 0 ? asset.name : asset.storage.url;
      let fileFormat = `image/${asset.meta.format}`;

      if (asset.meta.isVideo) {
        fileFormat = "video/mp4"; //hardcode until we have a good way of getting mime.
      }

      const file = new File([resp.data], filename, {
        type: `${fileFormat}`,
      }) as File & Record<string, unknown>;
      file.imgURL = `${asset.thumbnail}?_`;
      file.thumbnailUrl = `${asset.thumbnail}?_`;
      file.meta = {
        ...asset.meta,
        size: asset.meta.size / 1000, // need this so size validation works later as it needs to be in kb
      };
      file.base_caption = asset.description;

      files.push(file);

      yield put({
        type: PRE_DOWNLOAD_ASSET_ROUTINE.SUCCESS,
        payload: {
          asset,
          file,
        },
      });
    }

    yield put({ type: SHOW_CREATE_POST_ROUTINE.SUCCESS, payload: { files } });
    loadingSpinnerService.hideLoading();
    modalService.createPostModal(
      {
        single: true,
        files: files,
      },
      () => console.log("create post")
    );
    // remove selection of posts
    yield put({ type: TOGGLE_SELECT_NONE_MEDIA });
  } catch (error) {
    console.error(error);
    Bugsnag.notify(error as Error);
    yield put({
      type: SHOW_CREATE_POST_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

export function* filterAssets(
  action: SagaAction<{
    tags: Array<{ value: string }>;
    name: string;
    description: string;
    sourceName: string;
    favorite: boolean;
    mediaType: string | null;
    sort: string;
    usage: string | null;
  }>
): Generator {
  const selectedCollection = (yield select(
    getSelectedCollection
  )) as MediaCollection;

  const pageSize = (yield select(getMediaLibraryPageSize)) as number;
  const {
    tags,
    name,
    description,
    sourceName,
    favorite,
    mediaType,
    sort,
    usage,
  } = action.payload;
  const { eventService } = ngDeps;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_FILTER_ASSETS,
    metadata: {
      tags: tags,
      name: name,
      favorite: favorite,
      mediaType: mediaType,
      sourceName: sourceName,
      usage: usage,
      sort: sort,
    },
  });
  // const { loadingSpinnerService } = ngDeps;
  yield put({ type: FILTER_ASSETS_ROUTINE.REQUEST });

  try {
    // loadingSpinnerService.showLoading();
    const entriesResp = (yield call(
      [mlApi, mlApi.getEntries] as [unknown, (...args: unknown[]) => unknown],
      selectedCollection._id,
      1,
      pageSize,
      tags,
      name,
      description,
      favorite,
      mediaType,
      sourceName,
      usage,
      true, // withDownloadUrl - always want this
      sort
    )) as { data: PaginatedEntries };
    yield put({
      type: FILTER_ASSETS_ROUTINE.SUCCESS,
      payload: {
        entries: entriesResp.data,
      },
    });
  } catch (error) {
    yield put({
      type: FILTER_ASSETS_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  } finally {
    // loadingSpinnerService.hideLoading();
  }
}

export function* changeAssetPagination(
  action: SagaAction<{ page: number; pageSize: number }>
): Generator {
  const { page, pageSize } = action.payload;
  const selectedCollection = (yield select(
    getSelectedCollection
  )) as MediaCollection;

  const {
    tags,
    name,
    description,
    favorite,
    mediaType,
    sourceName,
    sort,
    usage,
  } = (yield select(getMediaLibraryFilters)) as MediaFilters;
  // const { loadingSpinnerService } = ngDeps;
  yield put({ type: CHANGE_ASSET_PAGINATION_ROUTINE.REQUEST });
  if (!selectedCollection?._id) {
    yield put({
      type: CHANGE_ASSET_PAGINATION_ROUTINE.SUCCESS,
      payload: {
        entries: {
          data: [],
          total: 0,
          page,
          pages: 0,
          limit: pageSize,
        },
      },
    });
    return;
  }

  try {
    // loadingSpinnerService.showLoading();
    const entriesResp = (yield call(
      [mlApi, mlApi.getEntries] as [unknown, (...args: unknown[]) => unknown],
      selectedCollection._id,
      page,
      pageSize,
      tags,
      name,
      description,
      favorite,
      mediaType,
      sourceName,
      usage,
      true, // withDownloadUrl - always want this
      sort
    )) as { data: PaginatedEntries };
    yield put({
      type: CHANGE_ASSET_PAGINATION_ROUTINE.SUCCESS,
      payload: {
        entries: entriesResp.data,
      },
    });
  } catch (error) {
    yield put({
      type: CHANGE_ASSET_PAGINATION_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  } finally {
    // loadingSpinnerService.hideLoading();
  }
}

export function* reloadTagsSaga(_action: SagaAction): Generator {
  const selectedCollection = (yield select(
    getSelectedCollection
  )) as MediaCollection;
  yield put({ type: RELOAD_ASSET_TAGS_ROUTINE.REQUEST });
  if (!selectedCollection?._id) {
    yield put({
      type: RELOAD_ASSET_TAGS_ROUTINE.SUCCESS,
      payload: {
        tags: [],
      },
    });
    return;
  }
  try {
    const tagsResp = (yield call(
      [mlApi, mlApi.getTags],
      selectedCollection._id
    )) as { data: CollectionTag[] };
    yield put({
      type: RELOAD_ASSET_TAGS_ROUTINE.SUCCESS,
      payload: {
        tags: formatTags(tagsResp.data),
      },
    });
  } catch (error) {
    yield put({
      type: RELOAD_ASSET_TAGS_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
  }
}

export function* applyBulkTags(
  action: SagaAction<{
    assets: CollectionFile[];
    tags: Array<{ value: string }>;
  }>
): Generator {
  const { assets, tags } = action.payload;
  const { eventService } = ngDeps;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_BULK_TAG_ASSETS,
    metadata: {
      count: assets.length,
    },
    useServices: ["AMP"],
  });

  yield put({ type: APPLY_BULK_TAGS_ROUTINE.REQUEST });
  try {
    for (const asset of assets) {
      const newTags = [...asset.tags];

      for (const tag of tags) {
        if (!newTags.includes(tag.value)) {
          newTags.push(tag.value);
        }
      }

      yield call(
        updateCollectionAsset,
        {
          type: UPDATE_COLLECTION_ASSET_ROUTINE.TRIGGER,
          payload: {
            asset,
            filename: asset.name,
            description: asset.description,
            tags: newTags,
          },
        },
        true
      );
    }
    yield call(reloadTagsSaga, reloadTags() as SagaAction);
    yield put({ type: APPLY_BULK_TAGS_ROUTINE.SUCCESS });
    toast.success(`Successfully updated ${assets.length} collection assets`);
  } catch (error) {
    yield put({
      type: APPLY_BULK_TAGS_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
    toast.error(`Failed to update collection asset`);
  }
}

export function* duplicateAssets(
  action: SagaAction<{
    sourceCollectionId: string;
    destinationCollectionIds: string[];
    assetIds: string[];
  }>
): Generator {
  const { sourceCollectionId, destinationCollectionIds, assetIds } =
    action.payload;
  const { eventService } = ngDeps;
  const collection = (yield select(getSelectedCollection)) as MediaCollection;

  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_DUPLICATE_ASSETS,
    metadata: {
      sourceCollectionId,
      destinationCollectionIds,
      count: assetIds.length,
    },
    useServices: ["AMP"],
  });

  yield put({ type: DUPLICATE_ASSETS_ROUTINE.REQUEST });
  try {
    const promises = destinationCollectionIds.map((collectionId) => {
      return call(
        duplicateCollectionAssets,
        sourceCollectionId,
        collectionId,
        assetIds
      );
    });

    yield all(promises);
    yield put({
      type: DUPLICATE_ASSETS_ROUTINE.SUCCESS,
      payload: {
        assetIds,
        sourceCollectionId,
        destinationCollectionIds,
      },
    });
    yield call(loadCollections, action);
    toast.success(
      `Successfully duplicated ${assetIds.length} collection asset${
        assetIds.length > 1 ? "s" : ""
      }`
    );
    yield put(selectCollection(collection._id, true));
  } catch (error) {
    yield put({
      type: DUPLICATE_ASSETS_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
    toast.error(`Failed to duplicate collection assets`);
  } finally {
    yield put(hideModal());
  }
}

export function* bulkMoveAssets(
  action: SagaAction<{ assets: CollectionFile[]; collectionId: string }>
): Generator {
  const { assets, collectionId } = action.payload;
  const { eventService } = ngDeps;
  const collection = (yield select(getSelectedCollection)) as MediaCollection;
  eventService.trackEvent({
    eventName: EVENTS.LIBRARY_BULK_MOVE_ASSETS,
    metadata: {
      count: assets.length,
    },
    useServices: ["AMP"],
  });

  yield put({ type: BULK_MOVE_ASSETS_ROUTINE.REQUEST });
  try {
    for (const asset of assets) {
      yield call([mlApi, mlApi.moveEntry], asset.asset_id, collectionId);
    }
    yield put({
      type: BULK_MOVE_ASSETS_ROUTINE.SUCCESS,
      payload: {
        assets,
        toCollectionId: collectionId,
        fromCollectionId: assets[0].inbox_id,
      },
    });
    toast.success(
      `Successfully moved ${assets.length} collection asset${
        assets.length > 1 ? "s" : ""
      }`
    );
    yield put(selectCollection(collection._id, true));
  } catch (error) {
    yield put({
      type: BULK_MOVE_ASSETS_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    handleError(error);
    toast.error(`Failed to move collection asset`);
  } finally {
    yield put(hideModal());
  }
}

function* reloadAssetCurrentPage(): Generator {
  const pagination = (yield select(getPagination)) as Pagination;
  const pageSize = (yield select(getMediaLibraryPageSize)) as number;
  // Keep the refetch aligned with the paginator state (currentPage is stored in redux)
  const currentPage = pagination?.currentPage ?? 1;
  yield put(changeAssetPaginationAction(currentPage, pageSize));
}

// -------------- WATCHERS ----------------------- //

export function* watchInitMediaLibraryPage(): Generator {
  yield takeEvery(
    INIT_MEDIA_LIBRARY_PAGE_ROUTINE.TRIGGER,
    initMediaLibraryPage
  );
}

export function* watchLoadCollection(): Generator {
  yield takeEvery(LOAD_COLLECTIONS_ROUTINE.TRIGGER, loadCollections);
}

export function* watchSelectCollection(): Generator {
  yield takeEvery(SELECT_COLLECTION_ROUTINE.TRIGGER, selectCollectionSaga);
}

export function* watchAddCollection(): Generator {
  yield takeEvery(ADD_COLLECTION_ROUTINE.TRIGGER, addCollection);
}

export function* watchUpdateCollection(): Generator {
  yield takeEvery(UPDATE_COLLECTION_ROUTINE.TRIGGER, updateCollection);
}

export function* watchDeleteCollectionUser(): Generator {
  yield takeEvery(DELETE_COLLECTION_USER_ROUTINE.TRIGGER, deleteCollectionUser);
}

export function* watchCreateOrUpdateCollectionUser(): Generator {
  yield takeEvery(
    CREATE_OR_UPDATE_COLLECTION_USER_ROUTINE.TRIGGER,
    createOrUpdateCollectionUser
  );
}

export function* watchAddCollectionAssetsFromFeed(): Generator {
  yield takeEvery(
    ADD_COLLECTION_ASSETS_FROM_FEED_ROUTINE.TRIGGER,
    addCollectionAssetsFromFeed
  );
}

export function* watchAddCollectionAssetsFromStock(): Generator {
  yield takeEvery(
    ADD_COLLECTION_ASSETS_FROM_STOCK_ROUTINE.TRIGGER,
    addCollectionAssetsFromStock
  );
}

export function* watchGetAllCollectionAssets(): Generator {
  yield takeEvery(
    GET_ALL_COLLECTION_ASSETS_ROUTINE.TRIGGER,
    getAllCollectionAssets
  );
}

export function* watchAddCollectionAssets(): Generator {
  yield takeEvery(ADD_COLLECTION_ASSETS_ROUTINE.TRIGGER, addCollectionAssets);
}

export function* watchDeleteCollectionAsset(): Generator {
  yield takeEvery(
    DELETE_COLLECTION_ASSET_ROUTINE.TRIGGER,
    deleteCollectionAsset
  );
}

export function* watchDeleteCollectionAssets(): Generator {
  yield takeEvery(
    DELETE_COLLECTION_ASSETS_ROUTINE.TRIGGER,
    deleteCollectionAssets
  );
}

export function* watchUpdateCollectionAsset(): Generator {
  yield takeEvery(
    UPDATE_COLLECTION_ASSET_ROUTINE.TRIGGER,
    updateCollectionAsset
  );
}

export function* watchShowCreatePost(): Generator {
  yield takeEvery(SHOW_CREATE_POST_ROUTINE.TRIGGER, showCreatePost);
}

export function* watchFilterAssets(): Generator {
  yield takeEvery(FILTER_ASSETS_ROUTINE.TRIGGER, filterAssets);
}

export function* watchChangeAssetPagination(): Generator {
  yield takeEvery(
    CHANGE_ASSET_PAGINATION_ROUTINE.TRIGGER,
    changeAssetPagination
  );
}

export function* watchReloadTags(): Generator {
  yield takeEvery(RELOAD_ASSET_TAGS_ROUTINE.TRIGGER, reloadTagsSaga);
}

export function* watchApplyBulkTags(): Generator {
  yield takeEvery(APPLY_BULK_TAGS_ROUTINE.TRIGGER, applyBulkTags);
}

export function* watchDeleteCollection(): Generator {
  yield takeEvery(DELETE_COLLECTION_ROUTINE.TRIGGER, deleteCollection);
}

export function* watchBulkMoveAssets(): Generator {
  yield takeEvery(BULK_MOVE_ASSETS_ROUTINE.TRIGGER, bulkMoveAssets);
}

export function* watchDuplicateAssets(): Generator {
  yield takeEvery(DUPLICATE_ASSETS_ROUTINE.TRIGGER, duplicateAssets);
}

export function* watchActionsToTrackByType(): Generator {
  yield takeEvery(TOGGLE_SIDEBAR_FILTERS, trackEventByType);
  yield takeEvery(SHOW_MODAL, trackEventByType);
}

export function* watchReloadAssetCurrentPage(): Generator {
  yield takeEvery(RELOAD_ASSET_CURRENT_PAGE, reloadAssetCurrentPage);
}
